import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { CourseListHeaderComponent } from '../../components/course-list-header/course-list-header.component';
import { CourseCardComponent } from '../../components/course-card/course-card.component';
import { CtaButtonComponent } from '../../../../shared/components/ui/cta-button/cta-button.component';
import { ModalComponent } from '../../../../shared/components/ui/modal/modal.component';
import { CourseCreatorComponent } from '../../components/course-creator/course-creator.component';
import { COURSE_CREATOR_BRIDGE } from '../../components/course-creator/bridges/course-creator-bridge.token';
import { SchoolCourseCreatorBridge } from '../../components/course-creator/bridges/school-course-creator.bridge';
import { EmptyCourseListComponent } from '../../components/empty-course-list/empty-course-list.component';
import { TopPerformanceComponent } from '../../components/top-performance/top-performance.component';
import { CourseService } from '../../services/course.service';
import { FilterChip } from '../../../../shared/interfaces/filter-chips.interface';
import { filter } from 'rxjs/operators';
import { challenge as ChallengeGen } from '@valvur/shared-grpc';

@Component({
  standalone: true,
  selector: 'app-my-courses',
  providers: [
    { provide: COURSE_CREATOR_BRIDGE, useClass: SchoolCourseCreatorBridge },
  ],
  imports: [
    CommonModule,
    TranslatePipe,
    CourseListHeaderComponent,
    CourseCardComponent,
    CtaButtonComponent,
    ModalComponent,
    CourseCreatorComponent,
    EmptyCourseListComponent,
    TopPerformanceComponent,
  ],
  templateUrl: './my-courses.component.html',
  styleUrl: './my-courses.component.scss',
})
export class MyCoursesComponent {
  #courseService = inject(CourseService);
  #router = inject(Router);

  isModalOpen = signal(false);
  selectedCourse = signal<ChallengeGen.Course | undefined>(undefined);

  draftCourses = this.#courseService.drafts;
  publishedCourses = this.#courseService.publishedCourses;
  hasDrafts = this.#courseService.hasDrafts;
  hasNotPublishedCourses = this.#courseService.hasNotPublishedCourses;

  courseCreatorStep = signal<string>('settings');
  #selectedFilter = signal<'all' | 'drafts' | 'published'>('all');

  filterChips: FilterChip[] = [
    { label: 'app.components.chips.all', isActive: true },
    { label: 'app.components.chips.drafts', isActive: false },
    { label: 'app.components.chips.published', isActive: false },
  ];

  onChipSelected(selected: FilterChip): void {
    this.filterChips = this.filterChips.map((chip) => ({
      ...chip,
      isActive: chip.label === selected.label,
    }));
    if (selected.label === 'app.components.chips.drafts') {
      this.#selectedFilter.set('drafts');
    } else if (selected.label === 'app.components.chips.published') {
      this.#selectedFilter.set('published');
    } else {
      this.#selectedFilter.set('all');
    }
  }

  showDraftsSection(): boolean {
    const f = this.#selectedFilter();
    return f === 'all' || f === 'drafts';
  }

  showPublishedSection(): boolean {
    const f = this.#selectedFilter();
    return f === 'all' || f === 'published';
  }

  filteredCoursesCount = computed(() => {
    const f = this.#selectedFilter();
    const drafts = this.draftCourses()?.length ?? 0;
    const published = this.publishedCourses()?.length ?? 0;
    if (f === 'drafts') return drafts;
    if (f === 'published') return published;
    return drafts + published;
  });

  constructor() {
    this.#watchMyCoursesPageEnabled();
    this.#syncSelectedCourse();
  }

  #syncSelectedCourse(): void {
    effect(() => {
      const currentCourse = this.selectedCourse();
      const courses = this.#courseService.courses();

      if (currentCourse && courses) {
        // Update selectedCourse with latest data from cache
        const updatedCourse = courses.find((c) => c.id === currentCourse.id);
        if (updatedCourse && updatedCourse !== currentCourse) {
          this.selectedCourse.set(updatedCourse);
        }
      }
    });
  }

  onOpenModal(): void {
    this.courseCreatorStep.set('settings');
    this.isModalOpen.set(true);
  }

  onOpenCourseCreator(courseId: string): void {
    const course = this.#courseService.getCourseById(courseId);
    this.selectedCourse.set(course);
    if (course && course.status === 'DRAFT') {
      this.courseCreatorStep.set('generate');
      this.isModalOpen.set(true);
    }
    if (course && course.status === 'READY_FOR_VIDEO_GENERATION') {
      this.courseCreatorStep.set('publish');
      this.isModalOpen.set(true);
    }
  }

  onOpenPublishedCourse(courseId: string): void {
    const course = this.#courseService.getCourseById(courseId);
    this.selectedCourse.set(course);
    if (course && course.status === 'PUBLISHED') {
      this.courseCreatorStep.set('assign');
      this.isModalOpen.set(true);
    }
  }

  #watchMyCoursesPageEnabled(): void {
    this.#courseService.schoolTeacherCoursesListsEnabled.set(true);

    this.#router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        const isOnMyCoursesPage = this.#router.url.includes(
          '/courses/my-courses',
        );
        this.#courseService.schoolTeacherCoursesListsEnabled.set(
          isOnMyCoursesPage,
        );
      });
  }
}
